/**
 * CommunityEmptyState — the empty-wall placeholder overlaid on the messages
 * region when the community has no messages (`!hasMore && messages.length === 0`).
 *
 * Flutter source of truth: `emptyList` (`community_screen.dart:1419-1472`):
 *  - Owner (`_isCurrentUser`) → the "Only subscribers can access your
 *    community. Together you can share ideas, photos, videos and go LIVE." copy
 *    plus a bordered "Share to get subscribers" button calling
 *    `_onShareSocialUser` (`community_screen.dart:1430-1466`).
 *  - Visitor → "Be the first to write in <name>'s community a message"
 *    (`community_screen.dart:1432`), no button.
 *
 * The owner's share button reuses the canonical `ExpauseButton`; the page wires
 * `onSharePress` to the same share handler as the app-bar share action (the web
 * `_onShareSocialUser`). l10n copy lives under `community.emptyState*` /
 * `community.shareToGetSubscribers` (Task 8).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import {
  ButtonVariant,
  ExpauseButton,
} from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface CommunityEmptyStateProps {
  /** True iff the current user is the wall owner (`community_screen.dart:1430`). */
  isCurrentUser: boolean;
  /**
   * Display name of the wall owner — substituted into the visitor empty-state
   * copy (`community.emptyStateVisitor` `{name}`, `community_screen.dart:1432`).
   */
  creatorName: string;
  /**
   * Invoked when the owner's "Share to get subscribers" button is tapped. The
   * host page wires this to the shared share handler (the web
   * `_onShareSocialUser`, `community_screen.dart:1440-1442`).
   */
  onSharePress: () => void;
}

export const CommunityEmptyState: React.FC<CommunityEmptyStateProps> = ({
  isCurrentUser,
  creatorName,
  onSharePress,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Owner vs visitor copy (`community_screen.dart:1430-1432`).
  const message = isCurrentUser
    ? l10n.community.emptyStateOwner
    : l10n.community.emptyStateVisitor.replace('{name}', creatorName);

  return (
    <div
      {...qaAttr({ id: 'community-empty-state' })}
      style={{
        // Flutter `Padding(EdgeInsets.all(32.0))` around the column
        // (`community_screen.dart:1423`).
        padding: responsive.spacing['3xl'],
        width: '100%',
        height: '100%',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          // `AppStyles.emptyCommunityStyle` — 16px / regular, centered.
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
          color: theme.text.primary,
          textAlign: 'center',
          maxWidth: '80%',
        }}
      >
        {message}
      </span>

      {/* Owner-only "Share to get subscribers" button
          (`community_screen.dart:1436-1466`). */}
      {isCurrentUser && (
        <div
          style={{
            // Flutter `Padding(left: 32, top: 32, right: 32)`
            // (`community_screen.dart:1438`).
            marginTop: responsive.spacing['3xl'],
            marginLeft: responsive.spacing['3xl'],
            marginRight: responsive.spacing['3xl'],
          }}
        >
          <ExpauseButton
            {...qaAttr({ id: 'community-share-to-subscribers' })}
            variant={ButtonVariant.primary}
            onClick={onSharePress}
          >
            {l10n.community.shareToGetSubscribers}
          </ExpauseButton>
        </div>
      )}
    </div>
  );
};
